// src/utils/assetImportTemplate.js

// Header order is a hard contract (authenticity check depends on it).
export const TEMPLATE_HEADERS = Object.freeze([
  "Asset Name",
  "Asset Description",
  "Asset Type",
  "Ownership Type",
  "Parent Asset ID",
  "Category Code",
  "Sub-Category Code",
  "Brand Name",
  "Location Code",
  "Status",
  "Serialization Type",
  "Manufacturer Serial Number",
  "Quantity",
  "Unit of Measurement",
  "Purchase Date",
  "Purchase Value",
  "Vendor Name",
  "Invoice Number",
  "Warranty Expiry",
  "Lease Start Date",
  "Lease End Date",
  "Lease Amount",
  "Lease Vendor Name",
  "Lease Agreement Number",
  "Lease Billing Frequency",
  "Rent Start Date",
  "Expected Return Date",
  "Rent Amount",
  "Rent Vendor Name",
  "Rent Agreement Number",
  "Rent Billing Frequency",
  "Sales Date",
  "Sales Value",
  "Customer ID",
  "Depreciation Method",
  "Useful Life (Years)",
  "Depreciation Rate (%)",
  "Residual Value",
  "Opening Book Value",
  "Maintenance Frequency",
  "Maintenance Start Date",
  "Assigned Department Name",
  "Assigned To (Email)",
  "Asset Owner (Email)",
]);

export const TEMPLATE_COLUMN_COUNT = TEMPLATE_HEADERS.length;

export const HEADER_TO_FIELD = Object.freeze({
  "Asset Name": "assetName",
  "Asset Description": "assetDescription",
  "Asset Type": "assetType",
  "Ownership Type": "ownershipType",
  "Parent Asset ID": "parentAssetId",
  "Category Code": "categoryCode",
  "Sub-Category Code": "subCategoryCode",
  "Brand Name": "brandName",
  "Location Code": "locationCode",
  "Status": "status",
  "Serialization Type": "serializationType",
  "Manufacturer Serial Number": "manufacturerSerialNumber",
  "Quantity": "quantity",
  "Unit of Measurement": "unitOfMeasurement",
  "Purchase Date": "purchaseDate",
  "Purchase Value": "purchaseValue",
  "Vendor Name": "vendorName",
  "Invoice Number": "invoiceNumber",
  "Warranty Expiry": "warrantyExpiry",
  "Lease Start Date": "leaseStartDate",
  "Lease End Date": "leaseEndDate",
  "Lease Amount": "leaseAmount",
  "Lease Vendor Name": "leaseVendorName",
  "Lease Agreement Number": "leaseAgreementNumber",
  "Lease Billing Frequency": "leaseBillingFrequency",
  "Rent Start Date": "rentStartDate",
  "Expected Return Date": "expectedReturnDate",
  "Rent Amount": "rentAmount",
  "Rent Vendor Name": "rentVendorName",
  "Rent Agreement Number": "rentAgreementNumber",
  "Rent Billing Frequency": "rentBillingFrequency",
  "Sales Date": "salesDate",
  "Sales Value": "salesValue",
  "Customer ID": "customerId",
  "Depreciation Method": "depreciationMethod",
  "Useful Life (Years)": "usefulLife",
  "Depreciation Rate (%)": "depreciationRate",
  "Residual Value": "residualValue",
  "Opening Book Value": "openingBookValue",
  "Maintenance Frequency": "maintenanceFrequency",
  "Maintenance Start Date": "maintenanceStartDate",
  "Assigned Department Name": "assignedDepartmentName",
  "Assigned To (Email)": "assignedToEmail",
  "Asset Owner (Email)": "ownerEmail",
});

// Columns whose values are sourced from a catalog. The Lists sheet is
// populated dynamically from singleValueListService / master services,
// exactly like the CreateEditAssetModal does — nothing hardcoded.
//
// Shape: { headerName: { listKey, valueMapper } }
//   listKey → key in the `masters` object returned by loadMasters()
//   valueMapper(entry) → string value written into the Lists column
export const DROPDOWN_SOURCES = Object.freeze({
  "Asset Type": {
    listKey: "assetTypes",
    valueMapper: (e) => e.value,
  },
  "Ownership Type": {
    listKey: "ownershipTypes",
    valueMapper: (e) => e.value,
  },
  "Category Code": {
    listKey: "categories",
    valueMapper: (e) => String(e.categoryCode || e.categoryId || e.code || "").trim(),
  },
  "Sub-Category Code": {
    listKey: "subCategories",
    valueMapper: (e) => String(e.subCategoryCode || e.subCategoryId || e.code || "").trim(),
  },
  "Location Code": {
    listKey: "locations",
    valueMapper: (e) => e.locationCode,
  },
  "Status": {
    listKey: "statuses",
    valueMapper: (e) => e.value,
  },
  // Serialization Type is intentionally NOT a free dropdown. It is derived
  // from (assetType, subCategory) via resolveEffectiveSerialPolicy — exactly
  // like the CreateEditAssetModal. We still expose the two legal values so the
  // user sees what to type, but validation will enforce the derived policy.
  "Serialization Type": {
    listKey: "serializationTypes", // injected as literal ["serialized","non-serialized"]
    valueMapper: (e) => e,
  },
  "Brand Name": {
    listKey: "brands",
    valueMapper: (e) => e.name || e.brandName,
  },
  "Vendor Name": {
    listKey: "vendors",
    valueMapper: (e) => e.vendorName,
  },
  "Lease Vendor Name": {
    listKey: "vendors",
    valueMapper: (e) => e.vendorName,
  },
  "Rent Vendor Name": {
    listKey: "vendors",
    valueMapper: (e) => e.vendorName,
  },
  "Depreciation Method": {
    listKey: "depMethods",
    valueMapper: (e) => e.value,
  },
  "Unit of Measurement": {
    listKey: "uoms",
    valueMapper: (e) => e.value,
  },
  "Maintenance Frequency": {
    listKey: "maintIntervals",
    valueMapper: (e) => e.value,
  },
  "Lease Billing Frequency": {
    listKey: "billingFreqs",
    valueMapper: (e) => e.value,
  },
  "Rent Billing Frequency": {
    listKey: "billingFreqs",
    valueMapper: (e) => e.value,
  },
  "Assigned Department Name": {
    listKey: "departments",
    valueMapper: (e) => e.departmentName || e.name,
  },
  "Assigned To (Email)": {
    listKey: "users",
    valueMapper: (e) => e.email,
  },
  // Asset Owner is only meaningful when ownership tracking is enabled
  // (preferencesService.isOwnershipEnabled). The template generator will
  // include this column's dropdown only when `masters.ownershipEnabled` is true.
  "Asset Owner (Email)": {
    listKey: "users",
    valueMapper: (e) => e.email,
    requiresOwnership: true,
  },
  "Customer ID": {
    listKey: "customers",
    valueMapper: (e) => e.customerId || e.id,
  },
});



export const DROPDOWN_MAP = Object.freeze(
  Object.keys(DROPDOWN_SOURCES).reduce((acc, header) => {
    if (TEMPLATE_HEADERS.includes(header)) acc[header] = header;
    return acc;
  }, {})
);